// ════════════════════════════════════════════════════════════════
// LOCK SCREEN — Master passphrase entry / creation
// ════════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { useVaultStore } from '../stores/vaultStore';
import type { AppConfig } from '../config/app.config';

export function LockScreen({ config }: { config: AppConfig }) {
  const [passphrase, setPassphrase] = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const { isInitialized, isAuthenticating, error, attemptUnlock, checkInitialized, clearError } =
    useVaultStore();

  useEffect(() => {
    checkInitialized();
  }, [checkInitialized]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isInitialized) {
      // First run: require confirmation
      if (passphrase.length < 8) {
        useVaultStore.setState({ error: 'Passphrase must be at least 8 characters.' });
        return;
      }
      if (passphrase !== confirmPass) {
        useVaultStore.setState({ error: 'Passphrases do not match.' });
        return;
      }
    }
    await attemptUnlock(passphrase);
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-vault-950 px-6">
      {/* Logo / App Name */}
      <div className="mb-8 text-center">
        <div
          className="w-20 h-20 rounded-3xl flex items-center justify-center text-3xl font-bold text-white mx-auto mb-4 shadow-lg"
          style={{ backgroundColor: config.app.accentColor }}
        >
          {config.app.shortName.charAt(0)}
        </div>
        <h1 className="text-xl font-semibold text-vault-100">{config.app.name}</h1>
        <p className="text-sm text-vault-500 mt-1">{config.app.description}</p>
      </div>

      {/* Lock Icon */}
      <div className="mb-6">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-accent-500">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-4">
        <div>
          <label className="vault-label">
            {isInitialized ? 'Master Passphrase' : 'Create Master Passphrase'}
          </label>
          <input
            type="password"
            value={passphrase}
            onChange={(e) => {
              setPassphrase(e.target.value);
              clearError();
            }}
            className="vault-input"
            placeholder="Enter your passphrase"
            autoFocus
            autoComplete="off"
          />
        </div>

        {!isInitialized && (
          <div>
            <label className="vault-label">Confirm Passphrase</label>
            <input
              type="password"
              value={confirmPass}
              onChange={(e) => setConfirmPass(e.target.value)}
              className="vault-input"
              placeholder="Re-enter your passphrase"
              autoComplete="off"
            />
          </div>
        )}

        {error && (
          <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-2">
            {error}
          </div>
        )}

        <button type="submit" disabled={isAuthenticating || !passphrase} className="vault-button-primary w-full">
          {isAuthenticating
            ? 'Unlocking...'
            : isInitialized
            ? 'Unlock Vault'
            : 'Create Vault'}
        </button>
      </form>

      {/* Security note */}
      <p className="text-xs text-vault-600 mt-6 text-center max-w-xs">
        Your passphrase encrypts all data locally using AES-256-GCM. It is never
        stored or transmitted. If you forget it, your data cannot be recovered.
      </p>

      {/* Disclaimer */}
      <div className="mt-4 text-xs text-vault-600 text-center max-w-sm border border-vault-800 rounded-lg p-3">
        This app is NOT a cryptocurrency wallet. It does not store private keys,
        connect to blockchains, or facilitate transactions. It is a personal
        inventory tracker for hardware wallet devices you already own.
      </div>
    </div>
  );
}
